import { ACTIVE_CATALOG } from "./market.js";
import { expScore, monthOk } from "./scoring.js";
import { isSuitable, type PartyConstraints } from "./suitability.js";
import { isLocked } from "./types.js";
import type {
  Axes,
  CityItinerary,
  Experience,
  PlannerCell,
  Profile,
  ScoredExperience,
  Uni,
} from "./types.js";

export const SLOTS: readonly string[] = ["Morning", "Afternoon", "Evening"];
export const AM_ONLY = new Set<string>([
  "shanghai-breakfast",
  "temple-of-heaven",
  "panda-base",
  "huangshan",
  "alishan",
  "yuanyang",
  "li-river",
  "jiming-wall",
]);
export const EVE_ONLY = new Set<string>([
  "mingsha-camel",
  "jingshan-sunset",
  "huangpu-cruise",
  "qinhuai-river",
  "wuzhen-night",
  "hongyadong",
  "the-bund",
  "night-markets-tw",
  "muslim-quarter",
  "lan-kwai-fong",
  "shanghai-bars",
  "xijiang-miao",
  "harbin-ice",
  "jiaozi-towers",
  "hutong-crawl",
  "star-ferry",
  "sichuan-hotpot",
  "hk-food",
  "jiufen",
]);
export const slotOf = (e: Experience): number =>
  AM_ONLY.has(e.id) ? 0 : EVE_ONLY.has(e.id) || e.u.nig >= 0.6 ? 2 : 1;
export const slotOk = (e: Experience, slot: number): boolean =>
  AM_ONLY.has(e.id) ? slot === 0 : EVE_ONLY.has(e.id) || e.u.nig >= 0.6 ? slot === 2 : true;

export interface PlannerOptions {
  /** Randomness added to candidate scores, for the "shuffle" action. */
  jitter?: number;
  prev?: CityItinerary[] | null;
  pinned?: Set<string> | null;
  rng?: () => number;
  /** Same party constraints used to build the profile. */
  constraints?: PartyConstraints;
}

export function buildPlanner(
  results: Profile,
  uni: Uni,
  axes: Axes,
  tripMonth: number,
  kids: boolean,
  opts: PlannerOptions = {},
): CityItinerary[] {
  const { jitter = 0, prev = null, pinned = null, rng = Math.random, constraints } = opts;
  const perDay = axes.energy.lean > 0.25 ? 3 : 2; // electric packs, serene breathes
  return results.cityPlan.map((cp, ci): CityItinerary => {
    const grid: PlannerCell[][] = Array.from({ length: results.per }, () => [null, null, null]);
    const lockedIds = new Set<string>();
    if (prev && pinned) {
      prev[ci]!.grid.forEach((day, d) =>
        day.forEach((e, sl) => {
          if (e && !isLocked(e) && pinned.has(e.id)) {
            grid[d]![sl] = e;
            lockedIds.add(e.id);
          }
        }),
      );
    }
    const countDay = (day: PlannerCell[]): number => day.filter(Boolean).length;
    const cands = ACTIVE_CATALOG.filter(
      (c) =>
        c.city === cp.city &&
        monthOk(c.months, tripMonth) &&
        (!kids || c.family >= 0.15) &&
        (!constraints || isSuitable(c, constraints)) &&
        !lockedIds.has(c.id),
    )
      .map((c) => ({ ...c, s: expScore(c, uni, axes) + (jitter ? rng() * jitter : 0) }))
      .sort((a, b) => b.s - a.s)
      .slice(0, results.per * perDay + 2);
    const place = (e: ScoredExperience): boolean => {
      const pref = slotOf(e);
      for (let d = 0; d < grid.length; d++)
        if (!grid[d]![pref] && countDay(grid[d]!) < perDay) {
          grid[d]![pref] = e;
          return true;
        }
      if (pref === 1)
        for (let d = 0; d < grid.length; d++)
          for (const sl of [0, 1])
            if (!grid[d]![sl] && countDay(grid[d]!) < perDay && slotOk(e, sl)) {
              grid[d]![sl] = e;
              return true;
            }
      return false;
    };
    cands.filter((e) => slotOf(e) !== 1).forEach(place);
    cands.filter((e) => slotOf(e) === 1).forEach(place);
    // one teaser slot per city only the sidekick can fill — placed last so nothing overwrites it
    lock: for (let d = 0; d < grid.length; d++) {
      for (let sl = 0; sl < 3; sl++)
        if (!grid[d]![sl]) {
          grid[d]![sl] = { locked: true };
          break lock;
        }
    }
    return { city: cp.city, grid };
  });
}
